/**
 * Canonical RBAC matrix for TurboIAM frontend.
 * Keep in sync with TurboIAMBackend/src/constants/rolePermissions.ts
 */
import type { UserRole } from '../types';
import { isSuperAdmin } from './roleAccess';

export const ALL_ROLES: readonly UserRole[] = [
  'super_admin',
  'app_admin',
  'iga_admin',
  'sso_admin',
  'pam_admin',
  'grc_admin',
  'auditor',
  'delegate',
  'user',
] as const;

/**
 * Route access map — single source for RoleRoute, Layout, Dashboard, VYSOR nav.
 * Routes not listed are open to any authenticated user.
 */
export const ROUTE_ACCESS: Record<string, readonly UserRole[]> = {
  '/users': ['super_admin', 'sso_admin'],
  '/dependency-graph': ['super_admin'],
  '/pam-dashboard': ['super_admin'],
  '/sso-review': ['super_admin', 'sso_admin'],
  '/sso-admin': ['super_admin', 'sso_admin'],
  '/risk-questions': ['super_admin', 'grc_admin'],
  '/risk-policies': ['super_admin', 'grc_admin'],
  '/risk-assessment-review': ['super_admin', 'grc_admin'],
  '/security': ['super_admin'],
  '/contacts': ALL_ROLES,
  '/audit-trail': ['super_admin', 'app_admin', 'iga_admin', 'sso_admin', 'pam_admin', 'auditor'],
  '/ai-governance': ['super_admin', 'app_admin', 'iga_admin', 'delegate', 'user', 'grc_admin'],
  '/applications': [
    'super_admin',
    'app_admin',
    'iga_admin',
    'sso_admin',
    'pam_admin',
    'grc_admin',
    'auditor',
    'delegate',
    'user',
  ],
  '/applications/iga-onboarding': ['super_admin', 'app_admin', 'iga_admin', 'delegate', 'user'],
  '/reports': [
    'super_admin',
    'app_admin',
    'iga_admin',
    'sso_admin',
    'pam_admin',
    'auditor',
    'delegate',
    'user',
  ],
  '/settings': ['super_admin', 'app_admin', 'iga_admin', 'sso_admin', 'pam_admin'],
};

export const APPLICATION_ENTERPRISE_READ_ROLES: readonly UserRole[] = [
  'super_admin',
  'app_admin',
  'iga_admin',
  'pam_admin',
  'grc_admin',
  'auditor',
] as const;

export const APPLICATION_CREATE_ROLES: readonly UserRole[] = ['super_admin'] as const;

/** VYSOR — register a new application (matches quick action `setup-app`). */
export const VYSOR_SETUP_APP_ROLES: readonly UserRole[] = APPLICATION_CREATE_ROLES;

/** VYSOR — Configure SSO wizard / tools (matches quick action `configure-sso`). */
export const VYSOR_CONFIGURE_SSO_ROLES: readonly UserRole[] = [
  'super_admin',
  'app_admin',
  'sso_admin',
  'delegate',
  'user',
] as const;

/** VYSOR — IGA entitlements wizard (matches quick action `iga-entitlements`). */
export const VYSOR_IGA_ENTITLEMENTS_ROLES: readonly UserRole[] = [
  'super_admin',
  'app_admin',
  'iga_admin',
  'delegate',
  'user',
] as const;

export type VysorWizardActionId = 'setup-app' | 'configure-sso' | 'iga-entitlements';

export function canRunVysorAction(
  role: string | undefined | null,
  action: VysorWizardActionId
): boolean {
  switch (action) {
    case 'setup-app':
      return roleCan(role, VYSOR_SETUP_APP_ROLES);
    case 'configure-sso':
      return roleCan(role, VYSOR_CONFIGURE_SSO_ROLES);
    case 'iga-entitlements':
      return roleCan(role, VYSOR_IGA_ENTITLEMENTS_ROLES);
    default:
      return false;
  }
}

export function roleCan(role: string | undefined | null, allowed: readonly UserRole[]): boolean {
  if (!role) return false;
  if (isSuperAdmin(role)) return true;
  return (allowed as readonly string[]).includes(role);
}

export function canAccessRoute(role: UserRole | string | undefined | null, pathname: string): boolean {
  if (!role) return false;
  if (isSuperAdmin(role)) return true;

  const base = pathname.split('?')[0].replace(/\/+$/, '') || '/';
  const path = base.startsWith('/user-profile/') ? '/user-profile' : base;
  const allowed = ROUTE_ACCESS[path];
  if (!allowed) return true;
  return allowed.includes(role as UserRole);
}

export function filterActionsByRouteAccess<T extends { path?: string }>(
  role: string | undefined,
  actions: T[]
): T[] {
  if (!role) return [];
  return actions.filter((action) => !action.path || canAccessRoute(role, action.path));
}

export function canManageTeamType(
  userRole: string | undefined,
  teamType: 'SSO' | 'PAM' | 'IGA'
): boolean {
  if (!userRole) return false;
  if (userRole === 'super_admin') return true;
  if (userRole === 'sso_admin' && teamType === 'SSO') return true;
  if (userRole === 'iga_admin' && teamType === 'IGA') return true;
  if (userRole === 'pam_admin' && teamType === 'PAM') return true;
  return false;
}
